import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import VerdictBar, { getPostVerdict } from '../components/VerdictBar';

export default function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [tab, setTab] = useState('all');
  const [results, setResults] = useState({ posts: [], users: [], topics: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query.trim()) return;
    setLoading(true);
    api.post('/search', { query, type: tab === 'all' ? 'all' : tab, limit: 20 })
      .then(r => setResults(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [query, tab]);

  const TABS = ['all', 'posts', 'users', 'topics'];

  const postVerdict = (post) => getPostVerdict(post);

  const VERDICT_COLOR = {
    verified:          'var(--color-verified)',
    invalidated:       'var(--color-invalidated)',
    disputed:          'var(--color-disputed)',
    new:               'var(--color-new)',
    mostly_verified:   'var(--color-verified)',
    mostly_invalidated:'var(--color-invalidated)',
    mostly_disputed:   'var(--color-disputed)',
    half_invalidated:  'var(--color-invalidated)',
    half_disputed:     'var(--color-disputed)',
    partly_verified:   'var(--color-verified)',
    partly_invalidated:'var(--color-invalidated)',
    partly_disputed:   'var(--color-disputed)',
    neutral:           'var(--color-text-muted)',
  };
  const VERDICT_LABEL = {
    verified:          'Verified',
    invalidated:       'Invalidated',
    disputed:          'Disputed',
    new:               'New',
    mostly_verified:   'Mostly Verified',
    mostly_invalidated:'Mostly Invalidated',
    mostly_disputed:   'Mostly Disputed',
    half_invalidated:  'Half Invalidated',
    half_disputed:     'Half Disputed',
    partly_verified:   'Partly Verified',
    partly_invalidated:'Partly Invalidated',
    partly_disputed:   'Partly Disputed',
    neutral:           'Unverified',
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Link to="/" style={{ color: 'var(--color-text-muted)', fontSize: 13, textDecoration: 'none', fontFamily: 'var(--font-body)' }}>
          ← Feed
        </Link>
        <h1 style={{
          color: 'var(--color-text-primary)',
          fontSize: 24, fontWeight: 700, margin: '8px 0 0',
          fontFamily: 'var(--font-display)',
        }}>
          Results for "{query}"
        </h1>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--color-border)' }}>
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '8px 16px', background: 'transparent', border: 'none',
              borderBottom: tab === t ? '2px solid var(--color-brand)' : '2px solid transparent',
              color: tab === t ? 'var(--color-brand)' : 'var(--color-text-muted)',
              cursor: 'pointer', fontSize: 13, fontWeight: tab === t ? 600 : 400,
              textTransform: 'capitalize', fontFamily: 'var(--font-body)',
              transition: 'color 0.15s ease',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {loading && (
        <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', fontFamily: 'var(--font-body)' }}>
          Searching...
        </p>
      )}

      {!loading && (
        <>
          {/* Posts */}
          {(tab === 'all' || tab === 'posts') && results.posts.length > 0 && (
            <section style={{ marginBottom: 28 }}>
              {tab === 'all' && (
                <h3 style={{
                  color: 'var(--color-text-muted)', fontSize: 11,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  marginBottom: 10, fontFamily: 'var(--font-body)', fontWeight: 700,
                }}>Posts</h3>
              )}
              {results.posts.map(post => {
                const verdict = postVerdict(post);
                return (
                  <div
                    key={post.id}
                    onClick={() => navigate(`/posts/${post.id}`)}
                    style={{
                      background: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-card)',
                      marginBottom: 10, overflow: 'hidden', cursor: 'pointer',
                      boxShadow: 'var(--shadow-card)',
                      transition: 'border-color 0.15s ease',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--color-border-strong)')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--color-border)')}
                  >
                    <VerdictBar
                      verdict={verdict}
                      counterArg={null}
                      moreCount={0}
                    />
                    <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {post.post_meta?.og_title && (
                          <p style={{
                            fontWeight: 700, color: 'var(--color-text-primary)',
                            margin: '0 0 4px', fontSize: 15,
                            fontFamily: 'var(--font-display)',
                          }}>
                            {post.post_meta.og_title}
                          </p>
                        )}
                        <p style={{
                          color: 'var(--color-text-secondary)', fontSize: 13,
                          margin: 0, lineHeight: 1.5, fontFamily: 'var(--font-body)',
                        }}>
                          {post.content?.slice(0, 120)}{post.content?.length > 120 ? '…' : ''}
                        </p>
                        <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                            by {post.username}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                            {post.argument_count || 0} arguments · {post.participant_count || 0} participants
                          </span>
                        </div>
                      </div>
                      {post.post_meta?.og_image && (
                        <img
                          src={post.post_meta.og_image}
                          alt=""
                          style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                          onError={e => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </section>
          )}

          {/* Users */}
          {(tab === 'all' || tab === 'users') && results.users.length > 0 && (
            <section style={{ marginBottom: 28 }}>
              {tab === 'all' && (
                <h3 style={{
                  color: 'var(--color-text-muted)', fontSize: 11,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  marginBottom: 10, fontFamily: 'var(--font-body)', fontWeight: 700,
                }}>Users</h3>
              )}
              {results.users.map(u => (
                <div
                  key={u.id}
                  style={{
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8, padding: '12px 16px', marginBottom: 8,
                    display: 'flex', alignItems: 'center', gap: 10,
                    boxShadow: 'var(--shadow-card)',
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'var(--color-brand-light)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, fontWeight: 700, color: 'var(--color-brand)', flexShrink: 0,
                    fontFamily: 'var(--font-body)',
                  }}>
                    {u.username[0].toUpperCase()}
                  </div>
                  <div>
                    <span style={{ color: 'var(--color-text-primary)', fontWeight: 600, fontSize: 14, fontFamily: 'var(--font-body)' }}>@{u.username}</span>
                    {u.created_at && (
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                        Joined {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </section>
          )}

          {/* Topics */}
          {(tab === 'all' || tab === 'topics') && results.topics.length > 0 && (
            <section style={{ marginBottom: 28 }}>
              {tab === 'all' && (
                <h3 style={{
                  color: 'var(--color-text-muted)', fontSize: 11,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  marginBottom: 10, fontFamily: 'var(--font-body)', fontWeight: 700,
                }}>Topics</h3>
              )}
              {results.topics.map(t => {
                const tVerdict = t.root_status === 'green' ? 'verified' : t.root_status === 'red' ? 'invalidated' : 'disputed';
                return (
                  <div
                    key={t.id}
                    onClick={() => navigate(`/posts/${t.root_post_id}`)}
                    style={{
                      background: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 8, padding: '12px 16px', marginBottom: 8,
                      cursor: 'pointer', boxShadow: 'var(--shadow-card)',
                      transition: 'border-color 0.15s ease',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--color-border-strong)')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--color-border)')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <p style={{ color: 'var(--color-text-primary)', fontSize: 14, margin: 0, fontWeight: 600, flex: 1, fontFamily: 'var(--font-body)' }}>
                        {t.topic_summary}
                      </p>
                      {t.root_status && (
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          color: VERDICT_COLOR[tVerdict],
                          background: VERDICT_COLOR[tVerdict] + '22',
                          padding: '2px 8px', borderRadius: 10, flexShrink: 0,
                          fontFamily: 'var(--font-body)',
                        }}>
                          {VERDICT_LABEL[tVerdict]}
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                      {t.argument_count} arguments
                    </span>
                  </div>
                );
              })}
            </section>
          )}

          {!loading && results.posts.length === 0 && results.users.length === 0 && results.topics.length === 0 && (
            <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 32, fontFamily: 'var(--font-body)' }}>
              No results for "{query}"
            </p>
          )}
        </>
      )}
    </div>
  );
}
